/**
 * Eric 2026-06-25 (iter126 P2) — Dashboard Bilan KB.
 *
 * Affiche le score de maturité de la base de connaissances de l'org +
 * breakdown des 5 sous-scores. Pour Concepteurs et Admins (canEditContent).
 *
 * Score 0-100, calculé backend (/api/knowledge/maturity-score) avec :
 *   - Couverture validée (40 pts)
 *   - Diversité des types (20 pts)
 *   - Fraîcheur 90j (20 pts)
 *   - Activité d'édition (10 pts)
 *   - Couverture vidéo (10 pts)
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { SpinnerGap, CheckCircle, Warning, ChartBar, Info } from "@phosphor-icons/react";

import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { canEditContent } from "../utils/permissions";
import { API_BASE as API } from "../lib/apiBase";

export default function BilanKBPage() {
  const { user } = useAuth();
  const { lang } = useLang();
  const canView = canEditContent(user);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!canView) return;
    (async () => {
      setLoading(true);
      try {
        const { data: res } = await axios.get(`${API}/api/knowledge/maturity-score`, {
          withCredentials: true,
        });
        setData(res);
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      }
      setLoading(false);
    })();
  }, [canView]);

  if (!canView) {
    return (
      <div className="p-8 text-center text-zinc-500" data-testid="bilan-kb-forbidden">
        {lang === "fr" ? "Accès réservé aux éditeurs." : "Editor access only."}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-12 text-center" data-testid="bilan-kb-loading">
        <SpinnerGap size={32} className="mx-auto animate-spin text-zinc-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8" data-testid="bilan-kb-error">
        <Warning size={28} className="mx-auto text-red-500" />
        <p className="text-center text-sm text-red-600 mt-2">{error}</p>
      </div>
    );
  }

  const scoreColor =
    data.score >= 80
      ? "text-teal-600"
      : data.score >= 60
      ? "text-blue-600"
      : data.score >= 40
      ? "text-amber-600"
      : "text-zinc-500";

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="bilan-kb-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight font-['Outfit']">
          <ChartBar size={24} weight="fill" className="inline mr-2 text-[#002FA7]" />
          {lang === "fr" ? "Bilan de la base de connaissances" : "Knowledge base health"}
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          {lang === "fr"
            ? "Indicateurs de maturité de votre base de connaissances"
            : "Maturity indicators for your knowledge base"}
        </p>
      </div>

      {/* Score principal */}
      <div className="bg-white rounded-2xl border-2 border-zinc-200 p-8 shadow-sm" data-testid="bilan-kb-main-score">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-zinc-400 mb-2">
              {lang === "fr" ? "Score de maturité" : "Maturity score"}
            </p>
            <p className={`text-6xl font-bold tracking-tight ${scoreColor}`} data-testid="bilan-kb-score-value">
              {data.score}
              <span className="text-2xl text-zinc-400">/100</span>
            </p>
            <p className="text-lg mt-2" data-testid="bilan-kb-level">
              {(lang === "en" ? data.level_label_en : data.level_label) || data.level_label}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-zinc-500 mb-2">
              {lang === "fr" ? "Statistiques globales" : "Global stats"}
            </p>
            <p className="text-2xl font-semibold">{data.stats.approved_kus} <span className="text-sm text-zinc-400 font-normal">/ {data.stats.total_kus}</span></p>
            <p className="text-xs text-zinc-500">{lang === "fr" ? "KUs validées" : "Approved KUs"}</p>
            <p className="text-xs text-zinc-500 mt-2">
              {data.stats.videos_with_kus} / {data.stats.total_videos} {lang === "fr" ? "vidéos couvertes" : "videos covered"}
            </p>
          </div>
        </div>
      </div>

      {/* Breakdown */}
      <div className="grid gap-3">
        <h2 className="text-base font-semibold text-zinc-700">
          {lang === "fr" ? "Détail des sous-scores" : "Score breakdown"}
        </h2>
        {Object.entries(data.breakdown).map(([key, b]) => {
          const pct = b.max > 0 ? (b.score / b.max) * 100 : 0;
          const barColor = pct >= 70 ? "bg-teal-500" : pct >= 40 ? "bg-amber-500" : "bg-red-400";
          return (
            <div key={key} className="bg-white rounded-lg border border-zinc-200 p-4" data-testid={`bilan-kb-breakdown-${key}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">{/* Tâche 1370 — libellé fabriqué au serveur : il porte sa version anglaise */}{(lang === "en" ? b.label_en : b.label) || b.label}</span>
                <span className="text-sm font-semibold text-zinc-700" data-testid={`bilan-kb-score-${key}`}>
                  {b.score}<span className="text-xs text-zinc-400">/{b.max}</span>
                </span>
              </div>
              <div className="w-full bg-zinc-100 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full ${barColor} transition-all`}
                  style={{ width: `${Math.min(pct, 100)}%` }}
                />
              </div>
              {key === "diversity" && data.stats.types_present.length > 0 && (
                <p className="text-[11px] text-zinc-500 mt-2">
                  <Info size={10} className="inline -mt-0.5 mr-1" />
                  {lang === "fr" ? "Types présents : " : "Types present: "}
                  {data.stats.types_present.join(", ")}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Tips */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900" data-testid="bilan-kb-tips">
        <p className="font-semibold flex items-center gap-2">
          <CheckCircle size={16} weight="fill" /> {lang === "fr" ? "Comment progresser ?" : "How to improve?"}
        </p>
        <ul className="mt-2 space-y-1 text-xs list-disc list-inside">
          {data.breakdown.coverage.ratio < 0.8 && (
            <li>{lang === "fr" ? "Validez plus de brouillons via la Validation rapide pour augmenter la couverture." : "Validate more drafts in Quick Validation to increase coverage."}</li>
          )}
          {data.breakdown.diversity.ratio < 1.0 && (
            <li>{lang === "fr" ? "Diversifiez les types : générez Q&R, Concepts, Scripts ET Procédures depuis vos vidéos." : "Diversify types: generate Q&A, Concepts, Scripts AND Procedures."}</li>
          )}
          {data.breakdown.freshness.ratio < 0.5 && (
            <li>{lang === "fr" ? "Mettez à jour les KUs anciennes (dernier 90 jours) pour rester pertinent." : "Update old KUs (last 90 days) to stay relevant."}</li>
          )}
          {data.breakdown.video_coverage.ratio < 0.8 && (
            <li>{lang === "fr" ? "Lancez les pipelines IA sur les vidéos sans KUs pour étendre la couverture." : "Run AI pipelines on videos without KUs to extend coverage."}</li>
          )}
          {data.score >= 80 && (
            <li className="text-teal-700 font-medium">{lang === "fr" ? "🏆 Excellent ! Continuez d'ajouter de la valeur." : "🏆 Excellent! Keep adding value."}</li>
          )}
        </ul>
      </div>
    </div>
  );
}
